import React, { type FC, type ReactNode, type SyntheticEvent, useCallback, useEffect } from "react"
import { createPortal } from "react-dom"
import useDevice from "@shared/hooks/useDevice"
import { useZIndex } from "@shared/hooks/useZIndex"
import type { IBtnProps } from "./CButtonGroup"
import { CButtonGroup } from "./CButtonGroup"
import { CloseIcon } from "../icons"
import { OnTopProvider, useEscapeOwnership } from "./escapeStack"
import { lockBodyScroll } from "./bodyScrollLock"

interface IProps {
  /** Флаг показа модального окна */
  isOpen: boolean
  /** Функция закрытия модального окна */
  onClose?: () => void
  /** Функция отправки формы */
  onSubmit?: () => void
  /** Дополнительный контент в заголовке */
  modalHeaderContent?: ReactNode
  /**
   * Полноширинная строка ПОД заголовком, внутри шапки (обе раскладки) —
   * например, лента дней рациона. modalHeaderContent для этого не годится:
   * на десктопе он рендерится справа, рядом с кнопкой закрытия.
   */
  headerBottomContent?: ReactNode
  /**
   * Стиль мобильной шапки: "gray" — серый фон с тенью (по умолчанию),
   * "plain" — белый фон, разделитель — обычная линия в 1px без тени.
   */
  headerVariant?: "gray" | "plain"
  /** Заголовок модального окна */
  title?: string | ReactNode
  /** Основной контент модального окна */
  modalBodyContent: ReactNode
  /**
   * Массив кнопок для футера. Кнопка `type: "save"` рендерится как submit внутри формы
   * контейнера (см. DivForm ниже), поэтому её обработчик передаётся через `onSubmit`, а не
   * через `action`: если задать оба, клик вызовет обработчик дважды — onClick и submit формы.
   */
  btns?: IBtnProps[]
  /** Серый фон */
  isBgGray?: boolean
  /** Является ли контейнер формой */
  isForm?: boolean
  /** CSS классы для body */
  wrapClassNameBody?: string
  /** Размер модального окна */
  size?: string
  /** Кастомный заголовок */
  headerCustom?: ReactNode | string
  /** ID корневого элемента для портала */
  rootElProps?: "modal" | "modal2"
  /** Дополнительные CSS классы */
  className?: string
  /** Можно ли закрыть модальное окно */
  dismissible?: boolean
  /** Закрывать по клику вне модального окна */
  closeOnBackdropClick?: boolean
  /** Закрывать по Escape */
  closeOnEscape?: boolean
  /**
   * Фиксированный z-index вместо динамического из useZIndex. Например,
   * панель рациона рендерится НИЖЕ сайдбара (z-999), чтобы навигация
   * оставалась кликабельной, пока панель открыта.
   */
  zIndex?: number
  /** Показывать кнопку закрытия */
  showCloseButton?: boolean
  /** Позиция модального окна */
  position?: "center" | "center-right" | "center-left" | "top" | "bottom"
  modalClassName?: string
}

/**
 * Базовый модальный компонент без зависимостей от Flowbite
 *
 * @example
 * // Простое модальное окно с кнопками
 * <CModalCore
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Редактирование данных"
 *   modalBodyContent={
 *     <div>
 *       <input type="text" placeholder="Название" />
 *       <textarea placeholder="Описание" />
 *     </div>
 *   }
 *   btns={[
 *     { type: "cancel", action: () => setIsOpen(false) },
 *     { type: "save", action: handleSave, loading: isLoading }
 *   ]}
 * />
 *
 * @example
 * // Модальное окно с формой
 * <CModalCore
 *   isOpen={isFormOpen}
 *   onClose={handleClose}
 *   onSubmit={handleSubmit}
 *   title="Создание задачи"
 *   isForm={true}
 *   size="lg"
 *   modalBodyContent={<TaskForm />}
 *   btns={[
 *     { type: "cancel", position: "left" },
 *     { type: "save", position: "right", loading: isSubmitting }
 *   ]}
 * />
 *
 * @example
 * // Большое модальное окно с кастомным заголовком
 * <CModalCore
 *   isOpen={isAnalysisOpen}
 *   onClose={() => setIsAnalysisOpen(false)}
 *   size="full"
 *   position="center"
 *   isBgGray={true}
 *   isForm={false}
 *   headerCustom={
 *     <div className="flex items-center justify-between w-full">
 *       <h2 className="text-xl font-bold">Анализ данных</h2>
 *       <div className="flex gap-2">
 *         <button>Экспорт</button>
 *         <button>Настройки</button>
 *       </div>
 *     </div>
 *   }
 *   modalBodyContent={<AnalysisComponent />}
 * />
 */
export const CModalCore: FC<IProps> = ({
  isOpen,
  onClose,
  onSubmit,
  modalHeaderContent,
  headerBottomContent,
  headerVariant = "gray",
  title,
  modalBodyContent,
  btns,
  isBgGray = false,
  isForm = true,
  wrapClassNameBody = "",
  size = "2xl",
  headerCustom,
  rootElProps = "modal",
  className = "",
  dismissible = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  zIndex: zIndexProp,
  showCloseButton = true,
  position = "center-right",
  modalClassName,
}) => {
  const { isMobile } = useDevice()
  const dynamicZIndex = useZIndex(isOpen)
  const zIndex = zIndexProp ?? dynamicZIndex
  const isTopmostOverlay = useEscapeOwnership(isOpen, zIndex)

  // Блокировка скролла при открытом модальном окне. Счётчик общий для всех окон: вложенное окно, закрывшись
  // (или смонтированное закрытым), не снимает блокировку, пока открыто окно под ним.
  useEffect(() => (isOpen ? lockBodyScroll() : undefined), [isOpen])

  // Обработка закрытия по Escape
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      // A nested popup (dropdown, autocomplete) that consumed this Escape marks
      // it with preventDefault in the capture phase — the modal must not also
      // react, or closing a dropdown would close the whole panel.
      if (event.defaultPrevented) return
      // Only the overlay on top reacts: an editor or a confirmation opened inside another window
      // stops the Escape at itself, or the window around it closes and takes the editor with it.
      if (!isTopmostOverlay()) return
      if (event.key === "Escape" && closeOnEscape && dismissible && onClose) {
        onClose()
      }
    },
    [onClose, closeOnEscape, dismissible, isTopmostOverlay],
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
      return () => document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, handleEscape])

  // If the user just finished a text-selection drag (e.g. selecting the modal
  // title and releasing the mouse outside the modal panel), the browser
  // synthesises a `click` on the closest common ancestor of mousedown/mouseup
  // — which is the overlay container. Without this guard that synthesised
  // click would close the modal even though the user was selecting text, not
  // clicking outside. A real outside click clears any existing selection on
  // mousedown, so checking for a non-empty selection at click time reliably
  // distinguishes the two cases.
  const isFinishingTextSelection = () => !!window.getSelection()?.toString()

  // Обработка клика по overlay
  const handleOverlayClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (isFinishingTextSelection()) return
    // Same mobile guard as handleBackdropClick below, for the same reason.
    // Tapping a textarea opens the virtual keyboard between touchstart and
    // touchend; the layout shifts under the finger, so the browser dispatches
    // the resulting `click` on the nearest common ancestor of the two — this
    // container. Reported symptom: "I tap the report field on Android and the
    // page disappears" (also on iOS). The panel covers the container fullscreen
    // on mobile, so no legitimate outside click can land here anyway; the close
    // button remains the way out.
    if (isMobile) return
    if (closeOnBackdropClick && dismissible && onClose && event.target === event.currentTarget) {
      onClose()
    }
  }

  // Обработка клика по backdrop
  const handleBackdropClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (isFinishingTextSelection()) return
    // On mobile the modal panel is rendered fullscreen (`w-full h-full`,
    // 100dvh), so the inset-0 backdrop is entirely covered by it — a
    // genuine tap on the backdrop is impossible. Android virtual-keyboard
    // open can briefly desync the layout viewport vs visual viewport and
    // expose the backdrop for one frame, which routes the user's tap to
    // it and closes the modal mid-edit. Reported symptom: "tap event
    // description, modal closes, calendar jumps to another date" because
    // the residual touch sequence lands on the now-exposed scroller.
    // Skip backdrop-driven close on mobile (`fit`-size modals would also
    // be covered on mobile via getPositionClasses → items-center, so the
    // same reasoning holds). The explicit close button still works.
    if (isMobile) return
    if (closeOnBackdropClick && dismissible && onClose) {
      onClose()
    }
  }

  // Получение размера модального окна
  const getSizeClasses = () => {
    const sizeMap: Record<string, string> = {
      xs: "max-w-xs",
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
      xl: "max-w-xl",
      "2xl": "max-w-2xl",
      "3xl": "max-w-3xl",
      "4xl": "max-w-4xl",
      "5xl": "max-w-5xl",
      "6xl": "max-w-6xl",
      "7xl": "max-w-7xl",
      full: "max-w-[calc(100%_-_50px)]", // 50px - sidebar width
      fit: "w-fit max-w-[90vw]",
    }
    return sizeMap[size] || sizeMap["2xl"]
  }

  // Получение позиции модального окна
  const getPositionClasses = () => {
    if (isMobile) {
      return "items-center justify-center" // На мобильном полноэкранный режим
    }

    const positionMap: Record<string, string> = {
      center: "items-center justify-center",
      "center-right": "items-center justify-end", // Убираем pr-8, модальное окно должно быть справа
      "center-left": "items-center justify-start",
      top: "items-start justify-center pt-16",
      bottom: "items-end justify-center pb-16",
    }
    return positionMap[position] || positionMap["center-right"]
  }

  // Получение анимации входа - простое появление без слайда
  const getAnimationClasses = () => {
    return "animate-in fade-in zoom-in-95 duration-200"
  }

  const bgColor = isBgGray ? "bg-customGray-100" : "bg-white"

  if (!isOpen || zIndex === 0) return null

  // Создаем портал для модального окна
  const rootEl = document.getElementById(rootElProps)
  const modalContent = (
    <div
      className={`
        fixed inset-0 flex
        ${getPositionClasses()}
        animate-in fade-in duration-200
        ${className}
      `}
      style={{ zIndex }}
      // Presentational: an outside click closes the modal, and the keyboard closes it with Escape.
      role="presentation"
      onClick={handleOverlayClick}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40"
        // The dimmed sheet behind the panel: decoration to a screen reader, and Escape is its keyboard.
        aria-hidden
        role="presentation"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className={`
          relative
          ${modalClassName}
          ${isMobile && size !== "fit" ? "w-full h-full" : getSizeClasses()}
          ${
            size === "full"
              ? `
            w-full h-full ${isMobile ? "min-h-[100dvh] max-h-[100dvh]" : "my-4"}
          `
              : position === "center-right"
                ? `
            h-full w-full ${isMobile ? "min-h-[100dvh] max-h-[100dvh]" : "max-w-2xl"}
          `
                : size === "fit"
                  ? getSizeClasses()
                  : `w-full ${getSizeClasses()}`
          }
          ${getAnimationClasses()}
          ${isMobile && size !== "fit" ? "rounded-none min-h-[100dvh] max-h-[100dvh]" : size === "full" ? "rounded-lg" : position === "center-right" ? "rounded-l-2xl" : "mx-4 rounded-lg"}
          ${position === "center-right" || size === "full" ? "h-full" : "max-h-[90vh]"}
          p-0 overflow-clip shadow-2xl
          ${bgColor}
        `}
        // Not a control: the stop keeps a click inside the panel from reaching the backdrop's close.
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        // React events bubble through the component tree, not the DOM tree —
        // without this stop, a right-click inside the modal would bubble up to
        // the parent (e.g. an ActivityCardShell) whose onContextMenu calls
        // preventDefault and shows the in-app menu. Stop here without
        // preventDefault so the browser's native context menu opens instead.
        onContextMenu={(e) => e.stopPropagation()}
      >
        <DivForm
          isForm={isForm}
          bgColor={bgColor}
          isFullHeight={(isMobile && size !== "fit") || position === "center-right" || size === "full"}
          onActionForm={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onSubmit?.()
          }}
        >
          {/* Header */}
          <div
            className={`
            ${
              isMobile
                ? `
              sticky top-0 left-0 right-0 z-50 pt-[calc(1rem+env(safe-area-inset-top))] pb-3
              ${headerVariant === "plain" ? "bg-white" : "bg-gray-100 shadow-md"}
            `
                : `
              pt-4 pb-3 rounded-t-lg
            `
            }
            px-4 border-b border-gray-200
          `}
          >
            {(() => {
              const titleNode = headerCustom ? (
                <div className={`font-bold text-gray-900 ${isMobile ? "text-lg" : "text-2xl"}`}>{headerCustom}</div>
              ) : (
                <div className="flex items-center gap-4">
                  {typeof title === "string" ? (
                    <div className={`font-bold text-gray-900 truncate ${isMobile ? "text-lg" : "text-2xl"}`}>
                      {title}
                    </div>
                  ) : (
                    title
                  )}
                </div>
              )

              const closeButton =
                showCloseButton && dismissible && onClose ? (
                  <button
                    onClick={onClose}
                    className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
                    type="button"
                  >
                    <CloseIcon size={20} />
                  </button>
                ) : null

              if (isMobile) {
                return (
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">{titleNode}</div>
                      {closeButton}
                    </div>
                    {modalHeaderContent && (
                      <div className="flex items-center gap-2 flex-wrap">{modalHeaderContent}</div>
                    )}
                  </div>
                )
              }

              return (
                <div className="flex flex-wrap items-center justify-between gap-2 w-full">
                  <div className="flex-1 min-w-0">{titleNode}</div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {modalHeaderContent}
                    {closeButton}
                  </div>
                </div>
              )
            })()}
            {headerBottomContent}
          </div>

          {/* Body */}
          <div
            className={`
              flex-1 overflow-x-hidden px-4 pt-2
              ${isMobile ? "max-md:px-2.5" : ""}
              ${wrapClassNameBody}
            `}
          >
            <div className="h-full">{modalBodyContent}</div>
          </div>

          {/* Footer with buttons */}
          {btns && (
            <div className="!p-0 w-full border-none">
              <CButtonGroup btns={btns} />
            </div>
          )}
        </DivForm>
      </div>
    </div>
  )

  // What the window shows learns whether another overlay covers it (the feed marks nothing it cannot see).
  const content = <OnTopProvider value={isTopmostOverlay}>{modalContent}</OnTopProvider>

  // Если есть rootEl, используем портал, иначе рендерим напрямую
  if (rootEl) {
    return createPortal(content, rootEl)
  }

  return content
}

// Вспомогательный компонент для формы/дива
const DivForm: FC<{
  children: ReactNode
  bgColor: string
  onActionForm: (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => void
  isForm?: boolean
  isFullHeight?: boolean
}> = ({ children, bgColor, onActionForm, isForm, isFullHeight = true }) => {
  const className = `${isFullHeight ? "h-screen-safe" : "h-full"} flex flex-col ${bgColor} max-md:pt-0`

  if (isForm) {
    return (
      <form
        className={className}
        onSubmit={onActionForm}
      >
        {children}
      </form>
    )
  }

  return <div className={className}>{children}</div>
}

export default CModalCore
